import { useEffect, useMemo, useState } from "react";
import { useCompareData } from "../components/compare/useCompareData";
import {
  PlayerSelectionCard,
  MobileSelection,
  PlayerSlotBar,
  PlayerSearch,
} from "../components/compare/PlayerSelectionBar";
import CompareGrid from "../components/compare/CompareGrid";
import CompareGridMobile from "../components/compare/CompareGridMobile";
import {
  CompareSkeletonDesktop,
  CompareSkeletonMobile,
} from "../components/compare/CompareSkeleton";
import SeasonSelector from "../components/compare/SeasonSelector";
import TransferHistory from "../components/compare/TransferHistory";
import {
  buildSeasonOptions,
  resolveEntryForPlayer,
  entryTeamLabel,
  defaultSeasonId,
} from "../components/compare/seasonOptions";
import { buildStatMap, isGoalkeeper, detectMode } from "../components/compare/metricDefs";
import { pushRecentPlayer } from "../components/compare/recentlyViewed";

// Compare players — full rebuild (TP_COMPARE_REBUILD_BRIEF.md).
// 2–4 players in ONE shared metric grid (metric = row, player = column),
// automatic field/GK mode with a mixed notice, a global SEASON selector with
// the resolved "Season · Team" shown per player column (Marin follow-up
// 2026-08-14), per-player transfer-history columns. Thin page: state + data orchestration
// here, all rendering in src/components/compare/*.

const MAX_PLAYERS = 4;

const MIXED_NOTICE =
  "Goalkeeper and field player selected — only the shared Overview block is comparable. Goalkeeping, attacking, passing and defending metrics are not shown across positions.";

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 60 ? age : null;
};

const Compare = () => {
  const [players, setPlayers] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [mobileAddOpen, setMobileAddOpen] = useState(false);
  // Grid view state lives HERE (not in the grids) so a loading extra player
  // never wipes expanded blocks or Total//90 toggles, and desktop/mobile stay
  // in sync across the md breakpoint.
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [per90Blocks, setPer90Blocks] = useState({});
  const { records, reload } = useCompareData(players);

  const toggleBlockExpanded = (blockId) =>
    setExpandedBlocks((s) => ({ ...s, [blockId]: !s[blockId] }));
  const setBlockPer90 = (blockId, value) =>
    setPer90Blocks((s) => ({ ...s, [blockId]: value }));

  const addPlayer = (player) => {
    if (!player || player.id === null || player.id === undefined) return;
    setPlayers((list) => {
      if (list.length >= MAX_PLAYERS || list.some((p) => p.id === player.id)) return list;
      return [...list, player];
    });
    pushRecentPlayer(player);
    setMobileAddOpen(false);
  };

  const removePlayer = (id) => setPlayers((list) => list.filter((p) => p.id !== id));

  const reset = () => {
    setPlayers([]);
    setSelectedOptionId(null);
    setMobileAddOpen(false);
    setExpandedBlocks({});
    setPer90Blocks({});
  };

  // Header data: profile endpoint enriches the search-result view model; a
  // failed profile leaves the search data standing (independent per player).
  const enriched = useMemo(
    () =>
      players.map((p) => {
        const prof = records[p.id]?.profile;
        const pp = prof?.player;
        return {
          ...p,
          name: pp?.name || p.name,
          imageUrl: pp?.image_url || p.imageUrl,
          club: prof?.team?.name || p.club,
          clubImage: prof?.team?.image_url || p.clubImage,
          role: pp?.role || undefined,
          tp_detailed_position: pp?.tp_detailed_position || p.tp_detailed_position,
          nationalities: pp?.nationalities || p.nationalities,
          nationality: pp?.nationality || p.nationality,
          birth_country: pp?.birth_country || p.birth_country,
          foot: pp?.foot || null,
          heightCm: pp?.height_cm || null,
          // Current market value {value, currency} sits on the profile
          // WRAPPER ({player, team, market_value}), not on player. Search
          // rows carry none, so the chip appears once the profile loads.
          marketValue: prof?.market_value || null,
          contractExpiration: pp?.contract_expiration || null,
          age: p.age ?? ageFromDob(pp?.date_of_birth),
        };
      }),
    [players, records],
  );

  const statsLists = useMemo(
    () => players.map((p) => records[p.id]?.stats || []),
    [players, records],
  );

  const anyLoading =
    players.length >= 2 && players.some((p) => !records[p.id] || records[p.id].loading);
  // Skeleton ONLY while nothing is displayable yet (initial load). While an
  // ADDITIONAL player loads, the grid stays mounted (keeping its view state)
  // and the new column simply fills in.
  const initialLoading =
    anyLoading && !players.some((p) => records[p.id] && !records[p.id].loading);

  // Honest per-column failure: profile fetch failed AND no stats arrived —
  // the column renders "Could not load player data" with a retry.
  const loadErrors = players.map((p) => {
    const rec = records[p.id];
    return !!(rec && !rec.loading && rec.profileError && !(rec.stats || []).length);
  });

  const seasonOptions = useMemo(() => buildSeasonOptions(statsLists), [statsLists]);

  // Default / repair the selected option whenever the option set changes.
  useEffect(() => {
    if (!seasonOptions.length) {
      if (selectedOptionId !== null) setSelectedOptionId(null);
      return;
    }
    if (!selectedOptionId || !seasonOptions.some((o) => o.id === selectedOptionId)) {
      setSelectedOptionId(defaultSeasonId(seasonOptions, statsLists));
    }
  }, [seasonOptions, selectedOptionId, statsLists]);

  const selectedOption = seasonOptions.find((o) => o.id === selectedOptionId) || null;
  const entries = statsLists.map((list) =>
    resolveEntryForPlayer(list, selectedOption?.season || null),
  );
  const statMaps = entries.map((e) => (e ? buildStatMap(e) : null));
  // Per-column "2025/26 · AC Milan" line — whose stats come from which team.
  const seasonTeamLabels = entries.map((e) =>
    e ? entryTeamLabel(selectedOption?.season || null, e) : null,
  );
  const gkFlags = enriched.map((p, i) => isGoalkeeper(records[p.id]?.profile, statsLists[i]));
  const mode = detectMode(gkFlags);
  const notice = mode === "mixed" ? MIXED_NOTICE : "";
  const excludeIds = players.map((p) => p.id);
  const transferColumns = enriched.map((p) => ({
    player: p,
    transfers: records[p.id]?.transfers || [],
  }));

  const seasonSelectorDesktop = (
    <SeasonSelector
      options={seasonOptions}
      selectedId={selectedOptionId}
      onSelect={setSelectedOptionId}
    />
  );
  const seasonSelectorMobile = (
    <SeasonSelector
      options={seasonOptions}
      selectedId={selectedOptionId}
      onSelect={setSelectedOptionId}
      variant="mobile"
    />
  );

  return (
    <div className="flex-1 bg-[#FBFCFE]">
      {/* ── Desktop ─────────────────────────────────────────────────── */}
      <div className="hidden md:block px-6 lg:px-10 py-7">
        {/* 2560px rule (frame 1p): content fluid to 1600px, then centered */}
        <div className="max-w-[1600px] mx-auto w-full flex flex-col gap-[18px]">
          <div className="flex items-end justify-between gap-6">
            <div className="flex flex-col gap-[5px]">
              <h1 className="text-2xl font-bold tracking-[-.025em] text-[#0F172A]">
                Compare players
              </h1>
              <p className="text-[13.5px] text-[#5A6879]">
                Pick two to four players — statistics are matched to their position automatically
              </p>
            </div>
            {players.length > 0 && players.length < 2 && (
              <button
                type="button"
                onClick={reset}
                className="text-[12.5px] font-semibold text-[#8494A8] hover:text-[#1B6FD6] pb-1"
              >
                Reset
              </button>
            )}
          </div>

          {players.length < 2 ? (
            <>
              <PlayerSelectionCard players={enriched} onAdd={addPlayer} onRemove={removePlayer} />
              <div className="flex flex-col items-center justify-center gap-[6px] h-[210px] border border-dashed border-[#D7DEE8] rounded-2xl bg-white/55">
                <div className="text-[14.5px] font-semibold text-[#5A6879]">
                  Add one more player to start comparing
                </div>
                <div className="text-[12.5px] text-[#8494A8]">
                  The metric grid appears as soon as two players are selected
                </div>
              </div>
            </>
          ) : (
            <>
              <PlayerSlotBar
                players={enriched}
                onAdd={addPlayer}
                onRemove={removePlayer}
                onReset={reset}
              />
              {initialLoading ? (
                <CompareSkeletonDesktop playerCount={players.length} />
              ) : (
                <>
                  <CompareGrid
                    players={enriched}
                    statMaps={statMaps}
                    seasonTeamLabels={seasonTeamLabels}
                    mode={mode}
                    notice={notice}
                    seasonSelector={seasonSelectorDesktop}
                    emptySeasonLabel={selectedOption?.season || null}
                    loadingSeason={anyLoading}
                    expanded={expandedBlocks}
                    per90={per90Blocks}
                    onToggleExpanded={toggleBlockExpanded}
                    onSetPer90={setBlockPer90}
                    loadErrors={loadErrors}
                    onRetry={reload}
                  />
                  <TransferHistory columns={transferColumns} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Mobile (own concept, frames 1h–1m) ──────────────────────── */}
      <div className="md:hidden bg-[#F5F7FA] min-h-full">
        {players.length < 2 ? (
          <MobileSelection
            players={enriched}
            onAdd={addPlayer}
            onRemove={removePlayer}
            onReset={reset}
          />
        ) : initialLoading ? (
          <CompareSkeletonMobile playerCount={players.length} />
        ) : (
          <>
            <CompareGridMobile
              players={enriched}
              statMaps={statMaps}
              seasonTeamLabels={seasonTeamLabels}
              mode={mode}
              notice={notice}
              seasonSelector={seasonSelectorMobile}
              emptySeasonLabel={selectedOption?.season || null}
              loading={anyLoading}
              onRemove={removePlayer}
              onReset={reset}
              onAddClick={() => setMobileAddOpen((v) => !v)}
              addOpen={mobileAddOpen && players.length < MAX_PLAYERS}
              searchNode={
                <PlayerSearch onPick={addPlayer} excludeIds={excludeIds} variant="mobile" autoFocus />
              }
              expanded={expandedBlocks}
              per90={per90Blocks}
              onToggleExpanded={toggleBlockExpanded}
              onSetPer90={setBlockPer90}
              loadErrors={loadErrors}
              onRetry={reload}
            />
            {/* pb-24 keeps the last transfer row clear of the platform-wide
                floating support button at 375px (UI rule 2). */}
            <div className="px-3 pb-24">
              <TransferHistory columns={transferColumns} variant="mobile" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Compare;
